import React, { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import type {
	useViewPageRovoActionsGetUserContentQuery$data,
	useViewPageRovoActionsGetUserContentQuery$variables,
} from './__generated__/useViewPageRovoActionsGetUserContentQuery.graphql.ts';
import type {
	useViewPageRovoActionsGetUserJobTitleQuery$data,
	useViewPageRovoActionsGetUserJobTitleQuery$variables,
} from './__generated__/useViewPageRovoActionsGetUserJobTitleQuery.graphql.ts';

import ClockIcon from '@atlaskit/icon/core/clock';
import CommentIcon from '@atlaskit/icon/core/comment';
import ListBulletedIcon from '@atlaskit/icon/core/list-bulleted';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import AiGenerativeAudioBriefingIcon from '@atlaskit/icon-lab/core/ai-generative-audio-briefing';
import AiGenerativeChaptersIcon from '@atlaskit/icon-lab/core/ai-generative-chapters';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import {
	type BaseConversationAction,
	type ConversationAction,
	type SkillAction,
	HandleActionType,
} from '@atlassian/rovo-conversation-actions';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';
import { useAudioPlayerStore } from '@atlassian/ai-audio';

import { FooterCommentsSummaryCombined } from '@confluence/ai-comments-summary/entry-points/FooterCommentsSummaryCombined.ts';
import { CommentsSummarySource } from '@confluence/ai-comments-summary/entry-points/CommentsSummaryContent.ts';
import { useCreateRovoExperienceSidebarController } from '@confluence/ai-rovo-button/entry-points/useCreateRovoExperienceSidebarController.ts';
import { useAudioButtonWrapperStore } from '@confluence/audio/entry-points/useAudioButtonWrapperStore.tsx';
import { useBulkIssueCreate } from '@confluence/bulk-issue-create/entry-points/useBulkIssueCreate.ts';
import {
	CommentCountsContext,
	CommentCountsProvider,
} from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { expVal, expValEquals, isExperimentEnabled } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import {
	useWarningDialogStateActions,
	WarningDialog,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateWarningDialogContextProvider.ts';
import { BULK_ISSUE_CREATE_SIDE_PANEL_ID } from '@confluence/issue-create-side-panel/entry-points/BulkCreateSidePanel.ts';
import { PageCatchupComponent } from '@confluence/page-catchup/entry-points/PageCatchupStreamingEntryPoint.tsx';
import { useRightSidebarContext } from '@confluence/page-layout-context';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { QuickSummaryStreamingComponent } from '@confluence/quick-summary/entry-points/QuickSummaryStreamingEntryPoint.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';
import type { PageRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.tsx';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';
import { getChildrenActionsForJobTitle } from '@confluence/experiment-rovo-chat-create-quick-actions/entry-points/createQuickActions.tsx';
import { shouldShowGetProjectOverviewAction } from '../utils/shouldShowGetProjectOverviewAction.ts';

export const messages = defineMessages({
	inlineCommentsMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.inline-comments',
		defaultMessage: 'Summarize comments',
		description: 'Label for the AI Content Actions Inline Comments Menu Item',
	},
	pageCatchupMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.page-catchup',
		defaultMessage: 'Summarize changes',
		description: 'Label for the AI Content Actions Page Catchup Menu Item',
	},
	pageCatchupMenuItemSubheaderLabel: {
		id: 'page-rovo-config-common.menu.item.label.page-catchup-subheader',
		defaultMessage: "See what's changed since your last visit",
		description: 'Label for the AI Content Actions Page Catchup Menu Item Subheader',
	},
	summarizeSectionLabel: {
		id: 'page-rovo-config-common.summarize.section.label',
		defaultMessage: 'Summarize page',
		description: 'Label for the summarize section in the menu group',
	},
	bulkIssueCreateMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.bulk-issue-create',
		defaultMessage: 'Create Jira issues',
		description: 'Label for the Jira Bulk Issue Create Menu Item',
	},
	bulkWorkItemsCreateMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.bulk-work-items-create',
		defaultMessage: 'Create Jira work items',
		description: 'Label for the Jira Bulk Work Items Create Menu Item',
	},
	exploreRelatedWorkAndUpdates: {
		id: 'page-rovo-config-common.menu.item.label.explore-related-items.non-final',
		defaultMessage: 'Explore related work and resources',
		description: 'Label for the Explore Related Items Menu Item',
	},
	audioBriefingMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.audio-briefing',
		defaultMessage: 'Listen to page',
		description:
			'The text is shown as the label for a drop-down menu item in the Rovo actions menu on the page view. When selected, it starts an audio briefing that reads the page content aloud to the user.',
	},
	audioBriefingMenuItemSubheaderLabel: {
		id: 'page-rovo-config-common.menu.item.label.audio-briefing-subheader',
		defaultMessage: 'Get a brief audio summary',
		description: 'Label for the Audio Briefing Menu Item Subheader',
	},
	createNewContentMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.create-new-content',
		defaultMessage: 'Create new content',
		description: 'Label for the Create New Content nested action Menu Item',
	},
	createNewContentActionHeading: {
		id: 'page-rovo-config-common.menu.item.heading.create-new-content',
		defaultMessage: 'Share your idea or start with an example',
		description: 'Heading for the Create New Content nested action menu',
	},
	getProjectOverviewMenuItemLabel: {
		id: 'page-rovo-config-common.menu.item.label.get-project-overview',
		defaultMessage: 'Get Project Overview',
		description: 'Label for the Get Project Overview skill action Menu Item',
	},
});

type ViewPageRovoActionsReturnType = {
	actionId: string;
	action: ConversationAction;
}[];

export const useViewPageRovoActions = (): ViewPageRovoActionsReturnType => {
	const {
		contentId,
		contentType,
		contentTitle,
		spaceKey,
		lastModifiedDate,
		isUnpublishedDraft,
		isEditorEmpty,
	} = useRovoConfigProviderProps() as PageRovoConfigProviderProps;

	const { isAdminHubAIEnabled, userId, isLicensed, isLoggedIn, isAIEarlyAccessEnabled } =
		useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { mode: rovoUiMode, toggleConversationAssistantUIMode } = useRovoExperienceAPIStore();
	const isRovoChatOpen = rovoUiMode !== undefined;
	const shouldSkipCreateNewContentAction =
		!isRovoChatOpen ||
		!isLicensed ||
		!isLoggedIn ||
		!fg('cc_fd_rovo_chat_create_quick_actions_kill_switch');

	const {
		data: userContentData,
		loading: userContentLoading,
		error: userContentError,
	} = useQueryInterop<
		useViewPageRovoActionsGetUserContentQuery$data,
		useViewPageRovoActionsGetUserContentQuery$variables
	>(
		graphql`
			query useViewPageRovoActionsGetUserContentQuery($cql: String!) {
				confluence_search(cql: $cql, first: 1) {
					count
				}
			}
		`,
		{
			variables: {
				cql: `type IN ('page', 'blogpost', 'embed', 'whiteboard', 'database') AND creator = '${userId}' AND created >= now('-28d')`,
			},
			skip: shouldSkipCreateNewContentAction,
			fetchPolicy: 'cache-first',
		},
	);

	const { data: userJobTitleData, error: userJobTitleError } = useQueryInterop<
		useViewPageRovoActionsGetUserJobTitleQuery$data,
		useViewPageRovoActionsGetUserJobTitleQuery$variables
	>(
		graphql`
			query useViewPageRovoActionsGetUserJobTitleQuery {
				user: confluence_atlassianUser(current: true) {
					id
					title
				}
			}
		`,
		{
			skip: shouldSkipCreateNewContentAction,
			fetchPolicy: 'cache-first',
		},
	);

	const hasCreatedContentInLast28Days = (userContentData?.confluence_search?.count ?? 0) > 0;

	const userJobTitle = userJobTitleData?.user?.title ?? null;

	const intl = useIntl();
	const { formatMessage } = intl;

	const { unresolvedInlineCommentsCount, footerCommentsCount } = useContext(CommentCountsContext);
	const pageMode = useGetPageMode();

	const rightSidebarContext = useRightSidebarContext();
	const { showWarningDialog } = useWarningDialogStateActions();
	const isSidePanelOpen =
		rightSidebarContext.getCurrent()?.panelId === BULK_ISSUE_CREATE_SIDE_PANEL_ID;

	const content = useRef<HTMLDivElement | null>(
		typeof document !== 'undefined' ? document.querySelector('div#content') : null,
	);

	// Page Catchup action

	const pageCatchupAction = useCallback(
		(): BaseConversationAction => ({
			id: 'summarize-changes-ai-action',
			title: formatMessage(messages.pageCatchupMenuItemLabel),
			secondaryTitle: formatMessage(messages.pageCatchupMenuItemSubheaderLabel),
			icon: () => <ClockIcon label="" testId="summarize-changes-ai-action.icon" />,
			viewConfig: {
				render: () => (
					<PageCatchupComponent
						contentId={contentId}
						contentType={contentType}
						spaceKey={spaceKey}
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
						showAIDisclaimer={false}
						allowCustomFollowUp
					/>
				),
			},
			type: 'ai-action',
			handleType: HandleActionType.Base,
			customFollowUpContext: {
				available: false,
				fetch: () => Promise.resolve([]),
			},
			nudgeContext: {
				id: 'content-catchup',
			},
		}),
		[formatMessage, contentId, contentType, spaceKey],
	);

	// Summarize page action

	const quickSummaryAction = useCallback(
		(): BaseConversationAction => ({
			id: 'summarize-page-ai-action',
			title: formatMessage(messages.summarizeSectionLabel),
			icon: () => <ListBulletedIcon label="" testId="summarize-page-ai-action.icon" />,
			viewConfig: {
				render: () => (
					<QuickSummaryStreamingComponent
						contentId={contentId}
						spaceKey={spaceKey}
						contentType={contentType}
						lastModifiedDate={lastModifiedDate ?? ''}
						source="view-page"
						setIsOpen={() => {}}
						contentPath={window.location.pathname}
						isInObjectSidebar
						showAIDisclaimer={false}
						allowCustomFollowUp
					/>
				),
			},
			type: 'ai-action',
			handleType: HandleActionType.Base,
			customFollowUpContext: {
				available: false,
				fetch: () => Promise.resolve([]),
			},
			nudgeContext: {
				id: 'page-summaries',
			},
		}),
		[formatMessage, contentId, contentType, spaceKey, lastModifiedDate],
	);

	// Summarize comments action

	const commentSummaryAction = useCallback(
		(): BaseConversationAction => ({
			id: 'summarize-comments-ai-action',
			title: formatMessage(messages.inlineCommentsMenuItemLabel),
			icon: () => <CommentIcon label="" />,
			viewConfig: {
				render: () => (
					<CommentCountsProvider contentId={contentId}>
						<FooterCommentsSummaryCombined
							contentId={contentId}
							contentType={contentType}
							source={CommentsSummarySource.Footer}
							spaceKey={spaceKey}
							isInObjectSidebar
							showAIDisclaimer={false}
						/>
					</CommentCountsProvider>
				),
			},
			type: 'ai-action',
			handleType: HandleActionType.Base,
		}),
		[formatMessage, contentId, contentType, spaceKey],
	);

	// Create Jira bulk issues action

	const handleBulkIssueCreate = useBulkIssueCreate({
		contentId,
		content,
		useRovoWidth: true,
	});

	const onBulkIssueCreate = useCallback(() => {
		if (isSidePanelOpen) {
			showWarningDialog(WarningDialog.FLOATING);
		} else {
			void handleBulkIssueCreate();
		}
	}, [isSidePanelOpen, showWarningDialog, handleBulkIssueCreate]);

	const bulkIssueCreateAction = useCallback(
		(): BaseConversationAction => ({
			id: 'bulk-issue-create-ai-action',
			title: formatMessage(
				fg('confluence-issue-terminology-refresh')
					? messages.bulkWorkItemsCreateMenuItemLabel
					: messages.bulkIssueCreateMenuItemLabel,
			),
			icon: () => <WorkItemsIcon label="" />,
			onClick: onBulkIssueCreate,
			type: 'ai-action',
			handleType: HandleActionType.Base,
		}),
		[formatMessage, onBulkIssueCreate],
	);

	// Audio briefing action

	const { openAudioPlayer } = useAudioPlayerStore();
	const sidebarApi = useCreateRovoExperienceSidebarController();
	const { activeContent } = useAudioButtonWrapperStore();

	const onAudioBriefingAction = useCallback(() => {
		if (!!activeContent) {
			openAudioPlayer(activeContent, { playImmediately: true });

			// Close the Rovo popup or sidebar after clicking
			const isSidebarOrMiniModalOpen =
				rovoUiMode === ConversationAssistantUIModes.SIDEBAR ||
				rovoUiMode === ConversationAssistantUIModes.MINI_MODAL;

			if (isSidebarOrMiniModalOpen) {
				// Close sidebar
				toggleConversationAssistantUIMode({
					targetMode: undefined,
					sidebarApi,
				});
			}
		}
	}, [activeContent, openAudioPlayer, rovoUiMode, toggleConversationAssistantUIMode, sidebarApi]);

	const audioBriefingAction = useCallback(
		(): BaseConversationAction => ({
			id: 'audio-briefing-ai-action',
			title: formatMessage(messages.audioBriefingMenuItemLabel),
			secondaryTitle: formatMessage(messages.audioBriefingMenuItemSubheaderLabel),
			icon: () => <AiGenerativeAudioBriefingIcon label="" />,
			onClick: onAudioBriefingAction,
			type: 'ai-action',
			handleType: HandleActionType.Base,
			nudgeContext: {
				id: 'proactive-audio-briefing', // PROACTIVE_AI_AUDIO_BRIEFING in convo-ai
			},
		}),
		[formatMessage, onAudioBriefingAction],
	);

	// Get Project Overview skill action

	const getProjectOverviewAction = useCallback(
		(): SkillAction => ({
			id: 'get-project-overview-ai-action',
			title: formatMessage(messages.getProjectOverviewMenuItemLabel),
			icon: () => <AiGenerativeChaptersIcon label="" />,
			handleType: HandleActionType.Skill,
			type: 'skill-action',
			staticPrompt: '/get-project-details',
		}),
		[formatMessage],
	);

	const audioSupportedByBrowser = useMemo(
		() => typeof MediaSource !== 'undefined' && MediaSource.isTypeSupported('audio/mpeg'),
		[],
	);

	const audioBriefingV3Enabled =
		audioSupportedByBrowser &&
		isAdminHubAIEnabled &&
		isExperimentEnabled('confluence_audio_v3') &&
		(!isExperimentEnabled('confluence_audio_labs_lozenge') || isAIEarlyAccessEnabled);

	const audioBriefingNudgeEnabled =
		audioBriefingV3Enabled &&
		(expValEquals('confluence_audio_v3_briefing_nudge', 'variant', 'nudge') ||
			expValEquals('confluence_audio_v3_briefing_nudge', 'variant', 'nudge_autoplay'));

	const shouldShowAudioBriefingAction = useMemo(() => {
		return !!activeContent && audioBriefingNudgeEnabled;
	}, [activeContent, audioBriefingNudgeEnabled]);

	// Final actions list

	const getViewPageRovoActions = useCallback(() => {
		const actions: { actionId: string; action: ConversationAction }[] = [];

		const isPageOrBlogpost = contentType === 'page' || contentType === 'blogpost';

		// Remove this const and use isEditorEmpty directly on experiment cleanup
		const isGatedEditorEmpty =
			isEditorEmpty &&
			expValEquals('platform_editor_remove_summarise_from_empty_doc', 'isEnabled', true);

		// Fire exposure for create quick actions AA experiment only when chat is open
		if (isRovoChatOpen && isLicensed && isLoggedIn) {
			expVal('cc_fd_rovo_chat_create_quick_actions_aa', 'isEnabled', false);
		}

		const createNewContentAction = (() => {
			if (
				shouldSkipCreateNewContentAction ||
				!expVal('cc_fd_rovo_chat_create_quick_actions', 'isEnabled', false)
			) {
				return null;
			}
			const childrenActions = getChildrenActionsForJobTitle(userJobTitle, intl);

			return {
				actionId: 'create-new-content',
				action: {
					id: 'create-new-content-nested-action',
					title: formatMessage(messages.createNewContentMenuItemLabel),
					icon: () => <AiGenerativeTextIcon label="" />,
					handleType: HandleActionType.Nested,
					children: childrenActions,
					actionHeading: formatMessage(messages.createNewContentActionHeading),
				},
			};
		})();

		// Only show create action when content query is not loading and has no error
		const canShowCreateAction = createNewContentAction && !userContentLoading && !userContentError;

		// If user has created content in the last 28 days, show create action as the first item
		if (hasCreatedContentInLast28Days && canShowCreateAction) {
			actions.push(createNewContentAction);
		}

		// needed for space overview since it doesn't have content type
		// also skip for unpublished drafts and empty live docs)
		if (!isPageOrBlogpost || isUnpublishedDraft || isGatedEditorEmpty) {
			// For non-page content types (like whiteboards), always include create action if available
			if (!hasCreatedContentInLast28Days && canShowCreateAction) {
				actions.push(createNewContentAction);
			}
			return actions;
		}

		// Get Project Overview action (top of the list for published pages with project-related title)
		if (
			shouldShowGetProjectOverviewAction({
				contentType,
				contentTitle,
			}) &&
			expVal('rovo_connie_empty_state_get_project_details_skill', 'isEnabled', false)
		) {
			actions.push({ actionId: 'get-project-overview', action: getProjectOverviewAction() });
		}

		actions.push(
			{ actionId: 'page-catchup', action: pageCatchupAction() },
			{ actionId: 'quick-summary', action: quickSummaryAction() },
		);

		// If user has NOT created content in the last 28 days, show create action as the 3rd item (after page-catchup and quick-summary)
		if (!hasCreatedContentInLast28Days && canShowCreateAction) {
			actions.push(createNewContentAction);
		}

		if (shouldShowAudioBriefingAction) {
			actions.push({ actionId: 'audio-briefing', action: audioBriefingAction() });
		}

		// Only show comment summary in Rovo button when proactive_comment_summary experiment is disabled
		if (
			(unresolvedInlineCommentsCount > 0 || footerCommentsCount > 0) &&
			!expValEquals('proactive_comment_summary', 'isEnabled', true)
		) {
			actions.push({ actionId: 'comment-summary', action: commentSummaryAction() });
		}

		if (pageMode === PageMode.VIEW && fg('confluence_ai_bulk_issue_creation')) {
			actions.push({ actionId: 'bulk-issue-create', action: bulkIssueCreateAction() });
		}

		return actions;
	}, [
		contentType,
		contentTitle,
		bulkIssueCreateAction,
		commentSummaryAction,
		footerCommentsCount,
		formatMessage,
		getProjectOverviewAction,
		hasCreatedContentInLast28Days,
		intl,
		isUnpublishedDraft,
		isEditorEmpty,
		pageCatchupAction,
		pageMode,
		quickSummaryAction,
		unresolvedInlineCommentsCount,
		audioBriefingAction,
		shouldShowAudioBriefingAction,
		userJobTitle,
		userContentLoading,
		userContentError,
		shouldSkipCreateNewContentAction,
		isRovoChatOpen,
		isLicensed,
		isLoggedIn,
	]);

	const actions = useMemo(() => getViewPageRovoActions(), [getViewPageRovoActions]);

	useEffect(() => {
		if (userContentError) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'viewPageRovoActions',
					action: 'errored',
					actionSubject: 'fetchUserContent',
					attributes: {
						error: (userContentError as Error)?.message || 'Unknown error',
					},
				},
			}).fire();
		}
	}, [userContentError, createAnalyticsEvent]);

	useEffect(() => {
		if (userJobTitleError) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					source: 'viewPageRovoActions',
					action: 'errored',
					actionSubject: 'fetchUserJobTitle',
					attributes: {
						error: (userJobTitleError as Error)?.message || 'Unknown error',
					},
				},
			}).fire();
		}
	}, [userJobTitleError, createAnalyticsEvent]);

	return actions;
};
